import { Deploy } from 'cordova-plugin-ionic/dist/ngx';
import { LoggingServiceModule } from 'ionic-logging-service';
import { annotation, arrowLeft, HeroIconModule, menu } from 'ng-heroicon';
import { SpeedTestModule } from 'ng-speed-test';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import {
	AngularFireRemoteConfig,
	AngularFireRemoteConfigModule,
	DEFAULTS,
	SETTINGS,
} from '@angular/fire/compat/remote-config';
import { FormsModule } from '@angular/forms';
import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppVersion } from '@awesome-cordova-plugins/app-version/ngx';
import { Camera } from '@awesome-cordova-plugins/camera/ngx';
import { Chooser } from '@awesome-cordova-plugins/chooser/ngx';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic/ngx';
import { DocumentViewer } from '@awesome-cordova-plugins/document-viewer/ngx';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { Geolocation } from '@awesome-cordova-plugins/geolocation/ngx';
import { HTTP } from '@awesome-cordova-plugins/http/ngx';
import { Keychain } from '@awesome-cordova-plugins/keychain/ngx';
import { MediaCapture } from '@awesome-cordova-plugins/media-capture/ngx';
import { Network } from '@awesome-cordova-plugins/network/ngx';
import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation/ngx';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { NativeStorage } from '@ionic-native/native-storage/ngx';
import { NetworkInterface } from '@ionic-native/network-interface/ngx';
import { IonicModule } from '@ionic/angular';
import { IonicStorageModule } from '@ionic/storage';
import { InputMaskModule } from '@ngneat/input-mask';
import { FormlyModule } from '@ngx-formly/core';
import * as Sentry from '@sentry/angular';
import { ConfigService } from '@shared/services/core/config.service';
import { RemoteConfigService } from '@shared/services/core/remote-config.service';

import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { InjectorService } from './injector.service';
import { EffectsModule } from './shared/app-effects/effects-module';
import { AppHttpInterceptor, ErrorInterceptor, JwtInterceptor } from './shared/interceptors';
import { NetworkService } from './shared/services/core/network.service';
import { VaultService } from './shared/services/core/vault.service';
import { SharedModule } from './shared/shared.module';
import { MatIconModule } from '@angular/material/icon';
import { RentalsApiClientModule } from "@vquip/api-clients";

const appInitVaultFactory =
	(service: VaultService): (() => Promise<void>) =>
	() =>
		service.init();
const appInitConfigFactory =
	(service: ConfigService): (() => Promise<void>) =>
	() =>
		service.init();
const appInitRemoteConfigFactory =
	(service: RemoteConfigService): (() => Promise<void>) =>
	() =>
		service.init();

@NgModule({
	imports: [
		BrowserModule,
		AppRoutingModule,
		HttpClientModule,
		FormsModule,
		SpeedTestModule,
		LoggingServiceModule,
		IonicModule.forRoot({
			swipeBackEnabled: false,
		}),
		IonicStorageModule.forRoot(),
		ServiceWorkerModule.register('ngsw-worker.js', {
			enabled: environment.production,
		}),
		BrowserAnimationsModule,
		HeroIconModule.withIcons({
			annotation,
			menu,
			arrowLeft,
		}),
		FormlyModule.forRoot({
			extras: { lazyRender: true },
		}),
		SharedModule,
		InputMaskModule.forRoot({
			inputSelector: 'input',
		}),
		EffectsModule.register([NetworkService]),
		AngularFireModule.initializeApp(environment.firebase),
		AngularFireRemoteConfigModule,
        MatIconModule,
        RentalsApiClientModule.forRoot({
            apiUrl: environment.rentalsApiEndPoint
        })
	],
	declarations: [AppComponent],
	providers: [
		InAppBrowser,
		NativeStorage,
		DocumentViewer,
		Network,
		Camera,
		Chooser,
		ScreenOrientation,
		AppVersion,
		Keychain,
		MediaCapture,
		NetworkInterface,
		Geolocation,
		HTTP,
		File,
		Diagnostic,
		Deploy,
		{ provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
		{ provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
		//{ provide: ErrorHandler, useClass: SentryIonicErrorHandler },
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AppHttpInterceptor,
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: () => () => {},
			deps: [Sentry.TraceService],
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: appInitVaultFactory,
			deps: [VaultService],
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: appInitConfigFactory,
			deps: [ConfigService],
			multi: true,
		},

		{
			provide: APP_INITIALIZER,
			useFactory: appInitRemoteConfigFactory,
			deps: [RemoteConfigService],
			multi: true,
		},
		{ provide: DEFAULTS, useValue: {} },
		{
			provide: SETTINGS,
			useFactory: () => ({ minimumFetchIntervalMillis: 10000 }),
		},
	],
	bootstrap: [AppComponent],
})
export class AppModule {
	constructor(private injector: Injector, private remoteConfig: AngularFireRemoteConfig) {
		InjectorService.injector = this.injector;
	}
}
